import communityHttpClient from './httpClient';
import COMMUNITY_ENDPOINTS from './endpoints';
import { normalizeListResponse, buildPaginationMeta } from './normalizers';

const BASE_URL = COMMUNITY_ENDPOINTS.POSTS;

export const getAll = (params = {}) =>
  communityHttpClient.get(BASE_URL, { params });

const listInFlight = new Map();

const serializeParamsStable = (params = {}) => {
  const keys = Object.keys(params).sort();
  const parts = [];

  for (const key of keys) {
    const value = params[key];
    if (value === undefined || value === null || value === "") continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
      }
      continue;
    }

    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }

  return parts.join("&");
};

// De-dupe identical list requests that happen at the same time (common in React 18 dev StrictMode)
export const getAllDeduped = (params = {}) => {
  const key = `${BASE_URL}?${serializeParamsStable(params)}`;
  const existing = listInFlight.get(key);
  if (existing) return existing;

  const req = communityHttpClient
    .get(BASE_URL, { params })
    .finally(() => {
      listInFlight.delete(key);
    });

  listInFlight.set(key, req);
  return req;
};

export const getBySlug = (slug) =>
  communityHttpClient.get(`${BASE_URL}/${slug}/`);

export const create = (data) =>
  communityHttpClient.post(`${BASE_URL}/`, data);

export const update = (slug, data) =>
  communityHttpClient.patch(`${BASE_URL}/${slug}/`, data);

export const remove = (slug, data) =>
  communityHttpClient.delete(`${BASE_URL}/${slug}/`, { data });

export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append('image', file);
  return communityHttpClient.post(COMMUNITY_ENDPOINTS.IMAGE_UPLOAD, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const toggleLike = (slug) =>
  communityHttpClient.post(`${BASE_URL}/${slug}/like/`);

export const incrementView = (slug) =>
  communityHttpClient.post(`${BASE_URL}/${slug}/increment_view/`);

export const getTrending = () =>
  communityHttpClient.get(`${BASE_URL}/trending/`);

export const getFeatured = () =>
  communityHttpClient.get(`${BASE_URL}/featured/`);

export const getTopics = () =>
  communityHttpClient.get(`${BASE_URL}/topics/`);

export const approve = (slug) =>
  communityHttpClient.post(`${BASE_URL}/${slug}/approve/`);

export const getAdminDashboard = () =>
  communityHttpClient.get(`${BASE_URL}/admin-dashboard/`);

let adminDashboardCache = {
  ts: 0,
  data: null,
  inFlight: null,
};

const ADMIN_DASHBOARD_INVALIDATE_EVENT = "community:adminDashboardInvalidated";

export const invalidateAdminDashboardCache = ({ broadcast = true } = {}) => {
  adminDashboardCache = {
    ts: 0,
    data: null,
    inFlight: null,
  };

  if (!broadcast) return;

  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent(ADMIN_DASHBOARD_INVALIDATE_EVENT));
  }
};

export const getAdminDashboardCached = ({ ttlMs = 5 * 60_000, force = false } = {}) => {
  const now = Date.now();

  if (!force && adminDashboardCache.data && now - adminDashboardCache.ts < ttlMs) {
    // Mimic an axios response shape for callers expecting `.data`.
    return Promise.resolve({ data: adminDashboardCache.data });
  }

  if (adminDashboardCache.inFlight) {
    return adminDashboardCache.inFlight;
  }

  adminDashboardCache.inFlight = getAdminDashboard()
    .then((res) => {
      adminDashboardCache = {
        ts: Date.now(),
        data: res?.data ?? null,
        inFlight: null,
      };
      return res;
    })
    .catch((err) => {
      adminDashboardCache.inFlight = null;
      throw err;
    });

  return adminDashboardCache.inFlight;
};

export const getNormalizedList = async (params = {}) => {
  const response = await getAll(params);
  const normalized = normalizeListResponse(response.data);
  const meta = buildPaginationMeta({
    response: normalized,
    page: typeof params.page === 'number' ? params.page : Number(params.page) || 1,
    pageSize: typeof params.page_size === 'number'
      ? params.page_size
      : Number(params.page_size || params.pageSize || 0)
  });

  return {
    data: normalized.results,
    meta
  };
};

export default {
  getAll,
  getAllDeduped,
  getBySlug,
  create,
  update,
  delete: remove,
  remove,
  uploadImage,
  toggleLike,
  incrementView,
  getTrending,
  getFeatured,
  getTopics,
  approve,
  getAdminDashboard,
  getAdminDashboardCached,
  invalidateAdminDashboardCache,
  getNormalizedList
};
